<!-- src/shine/components/functions/function-general-settings/function-general-settings.component.html -->

<div class="general-settings d-sm-flex flex-column" [formGroup]="settingsForm">
    <h3>Function Settings</h3>

    <!-- Type Selection -->
    <mat-form-field appearance="fill" class="full-width">
        <mat-label>Type</mat-label>
        <mat-select formControlName="type">
            @for (typeOption of functionTypes; track typeOption) {
                <mat-option [value]="typeOption">
                    {{ typeOption }}
                </mat-option>
            }
        </mat-select>
    </mat-form-field>

    @if (settingsForm.get('type')?.value === 'ProductCard') {
        <p class="group-hint">
            ProductCard resolves the requested SKU through the configured product source and emits
            <code>product-card.v1</code>. Product identity, URL, variant, availability, price, and currency remain
            function/feed authoritative. Presentation follows the chatbot fixed or bounded-dynamic policy.
        </p>
    }

    @if (settingsForm.get('type')?.value === 'Workflow') {
        <p class="group-hint">
            A workflow may render cards only from a structured product-card output mapping. Raw workflow prose is never interpreted as cards.
        </p>
    }

    <!-- Endpoint Field -->
    @if (settingsForm.get('type')?.value === 'Logic App') {
        <mat-form-field appearance="fill" class="full-width">
            <mat-label>Endpoint</mat-label>
            <input formControlName="endpoint" matInput placeholder="Enter endpoint URL">
    </mat-form-field>
    }

    <!-- Bearer Key Field -->
    @if (settingsForm.get('type')?.value === 'Logic App') {
        <mat-form-field appearance="fill" class="full-width">
            <mat-label>Bearer Key</mat-label>
            <input autocomplete="new-password" formControlName="bearerKey" matInput placeholder="Enter bearer key"
                   type="password">
            <mat-hint>Clearing detaches the function from its stored credential; secret retention cleanup is a separate governed operation.</mat-hint>
    </mat-form-field>
    }

    @if (settingsForm.get('type')?.value === 'Logic App') {
        <mat-form-field appearance="fill" class="full-width">
            <mat-label>Assistant media hosts</mat-label>
            <textarea formControlName="assistantMediaHosts" matInput rows="3"
                      placeholder="cdn.example.com&#10;images.example.net"></textarea>
            <mat-hint>
                Optional exact host allowlist for editorial images returned by this function. One host per line;
                no schemes, paths, ports, wildcards, or private hosts. Empty keeps function images blocked.
            </mat-hint>
            @if (settingsForm.get('assistantMediaHosts')?.hasError('invalidHost')) {
                <mat-error>Enter only exact public DNS hosts.</mat-error>
            } @else if (settingsForm.get('assistantMediaHosts')?.hasError('duplicateHost')) {
                <mat-error>Remove duplicate hosts.</mat-error>
            } @else if (settingsForm.get('assistantMediaHosts')?.hasError('tooManyHosts')) {
                <mat-error>At most 20 hosts are supported.</mat-error>
            }
        </mat-form-field>
    }

    @if (settingsForm.get('type')?.value === 'Logic App') {
        <mat-form-field appearance="fill" class="full-width">
            <mat-label>Argument lookups (JSON)</mat-label>
            <textarea formControlName="argumentLookupsJson" matInput rows="10"
                      placeholder='[{"sourceArgument":"market","targetArgument":"marketId","values":{"Norway":"47"},"removeSourceArgument":true}]'></textarea>
            <mat-hint>
                Optional deterministic source-to-target translations applied immediately before the external call.
                Keys and values must be non-empty. Source and target entries may repeat; normalized mapping-key aliases
                are allowed only when their exact mapped value matches.
                Set required to false for optional filters. For scoped mappings, use scopeArgument and scopedValues
                instead of values; lookups run in order.
            </mat-hint>
            @if (settingsForm.get('argumentLookupsJson')?.hasError('invalidArgumentLookups')) {
                <mat-error>Check lookup fields, types and mapping mode. Use values, or scopeArgument with scopedValues and empty values.</mat-error>
            } @else if (settingsForm.get('argumentLookupsJson')?.hasError('emptyArgumentLookup')) {
                <mat-error>Lookup arguments and mapping keys/values must be non-empty.</mat-error>
            } @else if (settingsForm.get('argumentLookupsJson')?.hasError('ambiguousArgumentLookup')) {
                <mat-error>Normalized mapping-key aliases cannot resolve to conflicting values.</mat-error>
            }
        </mat-form-field>
    }

    @if (settingsForm.get('type')?.value === 'Workflow') {
        <app-function-workflow-binding-selector
            [binding]="workflowBinding"
            (bindingChange)="updateWorkflowBinding($event)">
        </app-function-workflow-binding-selector>
    }

    @if (settingsForm.get('type')?.value === 'UI') {
        <h4>Direct form submission</h4>
        <mat-checkbox formControlName="formSubmissionEnabled">
            Send validated form fields directly to one configured Logic App
        </mat-checkbox>
        <p class="group-hint">
            This bypasses model reconstruction. Select one eligible saved Logic App and configure its exact HubSpot
            presentation. Both identities are checked again before rendering or external execution. Clearing this
            option removes both blocks through the same ETag-protected save.
        </p>
        @if (settingsForm.get('formSubmissionEnabled')?.value) {
            <mat-form-field appearance="fill" class="full-width">
                <mat-label>Configured Logic App target</mat-label>
                <mat-select formControlName="formSubmissionFunctionId"
                            (selectionChange)="updateFormSubmissionTarget($event.value)">
                    @for (target of formSubmissionTargets; track target.id) {
                        <mat-option [value]="target.id">
                            {{ target.name }} — {{ target.toolName }} (v{{ target.version }})
                        </mat-option>
                    }
                </mat-select>
                <mat-hint>Only enabled governed Logic Apps with provider-call-ID idempotency are offered.</mat-hint>
                @if (settingsForm.get('formSubmissionFunctionId')?.hasError('targetUnavailable')) {
                    <mat-error>Select an available governed Logic App target.</mat-error>
                }
            </mat-form-field>
            <div class="d-sm-flex gap-2">
                <mat-form-field appearance="fill">
                    <mat-label>Target version</mat-label>
                    <input formControlName="formSubmissionFunctionVersion" matInput type="number" readonly>
                </mat-form-field>
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Target tool name</mat-label>
                    <input formControlName="formSubmissionToolName" matInput readonly>
                </mat-form-field>
            </div>
            <mat-form-field appearance="fill" class="full-width">
                <mat-label>Target contract version</mat-label>
                <input formControlName="formSubmissionTargetContractVersion" matInput readonly>
                <mat-hint>Direct form submission requires governed v1 POST and provider-call-ID idempotency.</mat-hint>
            </mat-form-field>
            <mat-form-field appearance="fill" class="full-width">
                <mat-label>Submitted field schema (closed JSON)</mat-label>
                <textarea formControlName="formSubmissionInputSchema" matInput rows="10" readonly></textarea>
                <mat-hint>Copied from the selected saved target and revalidated before external I/O.</mat-hint>
                @if (settingsForm.get('formSubmissionInputSchema')?.hasError('closedSchema')) {
                    <mat-error>Enter a closed object schema with properties and additionalProperties=false.</mat-error>
                }
            </mat-form-field>
            <h4>HubSpot form presentation</h4>
            <div class="d-sm-flex gap-2">
                <mat-form-field appearance="fill">
                    <mat-label>Region</mat-label>
                    <mat-select formControlName="hubSpotFormRegion">
                        <mat-option value="eu1">EU (eu1)</mat-option>
                        <mat-option value="na1">North America (na1)</mat-option>
                    </mat-select>
                </mat-form-field>
                <mat-form-field appearance="fill" class="full-width">
                    <mat-label>Portal ID</mat-label>
                    <input formControlName="hubSpotFormPortalId" matInput autocomplete="off">
                    <mat-hint>Digits only, without leading zeroes.</mat-hint>
                </mat-form-field>
            </div>
            <mat-form-field appearance="fill" class="full-width">
                <mat-label>Form ID</mat-label>
                <input formControlName="hubSpotFormId" matInput autocomplete="off"
                       placeholder="aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee">
                <mat-hint>Canonical lowercase HubSpot form UUID.</mat-hint>
            </mat-form-field>
        }
    }

    @if (settingsForm.get('type')?.value === 'Email') {
        <mat-form-field appearance="fill" class="full-width">
            <mat-label>Recipient</mat-label>
            <input formControlName="emailRecipient" matInput type="email"
                   autocomplete="off" placeholder="team@example.com">
            <mat-hint>The model cannot change this destination.</mat-hint>
            @if (settingsForm.get('emailRecipient')?.hasError('required')) {
                <mat-error>A recipient is required.</mat-error>
            } @else if (settingsForm.get('emailRecipient')?.hasError('email')) {
                <mat-error>Enter a valid email address.</mat-error>
            }
        </mat-form-field>

        <mat-form-field appearance="fill" class="full-width">
            <mat-label>Subject</mat-label>
            <input formControlName="emailSubject" matInput maxlength="200"
                   placeholder="New customer inquiry">
            <mat-hint>Fixed subject; template placeholders are not supported.</mat-hint>
            @if (settingsForm.get('emailSubject')?.hasError('required')) {
                <mat-error>A subject is required.</mat-error>
            } @else if (settingsForm.get('emailSubject')?.hasError('pattern')) {
                <mat-error>The subject must be one line without template placeholders.</mat-error>
            }
        </mat-form-field>
    }

    <!-- Wait Message Field -->
    <h4>Governed contract</h4>
    <mat-form-field appearance="fill" class="full-width">
        <mat-label>Contract version</mat-label>
        <input formControlName="contractVersion" matInput readonly>
        <mat-hint>Blank means the existing legacy contract remains unchanged until a separately planned migration.</mat-hint>
    </mat-form-field>
    @if (!settingsForm.get('contractVersion')?.value) {
        <p class="group-hint">
            Legacy function: ordinary edits preserve its current request contract. Migrate its Logic App and configuration together only after compatibility testing.
        </p>
    } @else {
    <mat-form-field appearance="fill" class="full-width">
        <mat-label>Output schema (closed JSON)</mat-label>
        <textarea formControlName="outputSchema" matInput rows="8"></textarea>
        <mat-hint>Use an object schema with properties and additionalProperties set to false.</mat-hint>
        @if (settingsForm.get('outputSchema')?.hasError('closedSchema')) { <mat-error>Enter a closed object JSON schema.</mat-error> }
    </mat-form-field>
    <mat-form-field appearance="fill" class="full-width">
        <mat-label>Authority</mat-label>
        <mat-select formControlName="authority">
            <mat-option value="configured_provider">Configured provider</mat-option>
            <mat-option value="shine_runtime">Shine runtime</mat-option>
            <mat-option value="delegated_downstream">Delegated downstream</mat-option>
        </mat-select>
    </mat-form-field>
    <p class="group-hint">
        Authentication is derived from the stored server-side credential reference and is never displayed as a value here.
        Workflow functions use the signed Connect reference.
    </p>
    @if (settingsForm.get('type')?.value === 'Logic App') {
        <h4>Request policy</h4>
        <div class="d-sm-flex gap-2">
            <mat-form-field appearance="fill">
                <mat-label>HTTP method</mat-label>
                <mat-select formControlName="requestMethod">
                    <mat-option value="POST">POST</mat-option>
                </mat-select>
                <mat-hint>Governed Logic Apps support POST only.</mat-hint>
            </mat-form-field>
            <mat-form-field appearance="fill">
                <mat-label>Argument placement</mat-label>
                <mat-select formControlName="requestArgumentPlacement">
                    <mat-option value="json_body">JSON body</mat-option>
                </mat-select>
                <mat-hint>Validated arguments are sent as one JSON object.</mat-hint>
            </mat-form-field>
        </div>
    }
    <div class="d-sm-flex gap-2">
        <mat-form-field appearance="fill"><mat-label>Timeout (ms)</mat-label><input formControlName="timeoutMs" matInput type="number"></mat-form-field>
        <mat-form-field appearance="fill"><mat-label>Maximum attempts</mat-label><input formControlName="maximumAttempts" matInput type="number"></mat-form-field>
        <mat-form-field appearance="fill"><mat-label>Initial backoff (ms)</mat-label><input formControlName="initialBackoffMs" matInput type="number"></mat-form-field>
        <mat-form-field appearance="fill"><mat-label>Maximum concurrency</mat-label><input formControlName="maximumConcurrency" matInput type="number"></mat-form-field>
    </div>
    <mat-form-field appearance="fill" class="full-width"><mat-label>Idempotency mode</mat-label><mat-select formControlName="idempotencyMode">@if (settingsForm.get('type')?.value !== 'Logic App') { <mat-option value="none">None</mat-option> }<mat-option value="provider_call_id">Provider call ID</mat-option></mat-select><mat-hint>Governed Logic Apps require a provider-enforced call ID contract; legacy functions remain unchanged until migrated.</mat-hint></mat-form-field>
    @if (settingsForm.get('idempotencyMode')?.value !== 'none') {
        <mat-form-field appearance="fill" class="full-width"><mat-label>Idempotency header name</mat-label><input formControlName="idempotencyHeaderName" matInput placeholder="Idempotency-Key">@if (settingsForm.get('idempotencyHeaderName')?.hasError('invalidIdempotencyHeader')) { <mat-error>Use a safe provider header name.</mat-error> }</mat-form-field>
    }
    }

    <!-- Wait Message Field -->
    <mat-form-field appearance="fill" class="full-width">
        <mat-label>Wait Message</mat-label>
        <input matInput formControlName="waitMessage" placeholder="Enter wait message">
    </mat-form-field>

    <!-- Parameters Field -->
    @if (settingsForm.get('type')?.value !== 'Email') {
        <mat-form-field appearance="fill" class="full-width">
            <mat-label>Parameters (JSON)</mat-label>
            <textarea formControlName="parameters" matInput placeholder='e.g., {"param1": "value1", "param2": "value2"}'
                      rows="15"></textarea>
            @if (settingsForm.get('parameters')?.hasError('required') && (settingsForm.get('parameters')?.dirty || settingsForm.get('parameters')?.touched)) {
                <mat-error>Parameters are required.</mat-error>
            }
            @if (settingsForm.get('parameters')?.hasError('invalidJson') && (settingsForm.get('parameters')?.dirty || settingsForm.get('parameters')?.touched)) {
                <mat-error>Parameters must be valid JSON.</mat-error>
            }
            @if (settingsForm.get('parameters')?.hasError('requiredPropertyMissing')) {
                <mat-error>A required field is missing from properties. Fix the schema before saving; affected assistants cannot use this function safely.</mat-error>
            }
        </mat-form-field>
    }
</div>
